import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Plus, Save, ArrowLeft, FilePlus } from 'lucide-react';
import {
  listSkills,
  getSkill,
  saveSkill,
  deleteSkill,
  type SkillResponse,
  type SkillSummary,
} from '@/lib/api';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PromptDialog } from '@/components/common/PromptDialog';
import { ResourceDeleteConfirm } from '@/components/common/ResourceDeleteConfirm';

interface SkillsPageProps {
  routePrefix?: string;
}

export function SkillsPage({
  routePrefix = '/admin/resources/skills',
}: SkillsPageProps): React.ReactElement {
  const { name } = useParams<{ name?: string }>();
  return name ? (
    <SkillEditor name={name} routePrefix={routePrefix} />
  ) : (
    <SkillListView routePrefix={routePrefix} />
  );
}

function SkillListView({ routePrefix }: { routePrefix: string }): React.ReactElement {
  const [skills, setSkills] = useState<SkillSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listSkills()
      .then(r => {
        setSkills(r.skills);
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-lg font-semibold text-text-primary">Skills</h1>
        <Link
          to={`${routePrefix}/new`}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="size-4" />
          New Skill
        </Link>
      </div>
      <div className="flex-1 overflow-auto px-4 pb-4 pt-2">
        {error && <div className="rounded-md bg-error/15 p-3 text-sm text-error">{error}</div>}
        {!skills && !error && <div className="text-sm text-text-tertiary">Loading…</div>}
        {skills?.length === 0 && <div className="text-sm text-text-tertiary">No skills yet.</div>}
        {skills && skills.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-secondary">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {skills.map(s => (
                <tr key={s.name} className="border-b border-border hover:bg-surface/50">
                  <td className="py-2 pr-4">
                    <Link
                      to={`${routePrefix}/${encodeURIComponent(s.name)}`}
                      className="text-primary"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-text-secondary">
                    {s.description ? (
                      <span className="line-clamp-1">{s.description}</span>
                    ) : (
                      <span className="text-text-muted">None</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <span className="rounded bg-surface-elevated px-2 py-0.5 text-xs">
                      {s.source}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-text-tertiary">{s.totalSizeBytes} B</td>
                  <td className="py-2 text-text-tertiary">{s.updatedAt.slice(0, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SkillEditor({
  name,
  routePrefix,
}: {
  name: string;
  routePrefix: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const isNew = name === 'new';
  const [skill, setSkill] = useState<SkillResponse | null>(null);
  const [skillName, setSkillName] = useState(isNew ? '' : name);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<Record<string, string>>(
    isNew ? { 'SKILL.md': '# Skill\n' } : {}
  );
  const [selected, setSelected] = useState<string>('SKILL.md');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addFileOpen, setAddFileOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    getSkill(name)
      .then(s => {
        setSkill(s);
        setDescription(s.description ?? '');
        const map: Record<string, string> = {};
        s.files.forEach(f => {
          map[f.path] = f.content;
        });
        setFiles(map);
        setSelected(
          Object.keys(map).includes('SKILL.md') ? 'SKILL.md' : (Object.keys(map)[0] ?? '')
        );
      })
      .catch((e: Error) => {
        setError(e.message);
      });
  }, [name, isNew]);

  const isBundled = skill?.source === 'bundled';

  async function handleSave(): Promise<void> {
    if (!skillName) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveSkill(skillName, { description: description || null, files });
      setSkill(saved);
      if (isNew) navigate(`${routePrefix}/${encodeURIComponent(skillName)}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    try {
      await deleteSkill(skillName);
      navigate(routePrefix);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function handleAddFile(path: string): string | true {
    if (files[path] !== undefined) {
      return `A file at "${path}" already exists in this skill.`;
    }
    setFiles(currentFiles => ({ ...currentFiles, [path]: '' }));
    setSelected(path);
    return true;
  }

  function handleDeleteFile(path: string): void {
    if (path === 'SKILL.md') return;
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(files)) {
      if (k !== path) next[k] = v;
    }
    setFiles(next);
    if (selected === path) setSelected('SKILL.md');
  }

  const sortedPaths = Object.keys(files).sort((a, b) => {
    if (a === 'SKILL.md') return -1;
    if (b === 'SKILL.md') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Link to={routePrefix} className="text-text-secondary hover:text-text-primary">
          <ArrowLeft className="size-4" />
        </Link>
        <input
          className="w-48 rounded bg-surface px-2 py-1 text-sm"
          value={skillName}
          onChange={e => {
            setSkillName(e.target.value);
          }}
          placeholder="skill-name"
          disabled={!isNew}
        />
        <input
          className="flex-1 rounded bg-surface px-2 py-1 text-sm"
          value={description}
          onChange={e => {
            setDescription(e.target.value);
          }}
          placeholder="Description"
          disabled={isBundled}
        />
        <button
          onClick={handleSave}
          disabled={saving || isBundled}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          <Save className="size-4" /> Save
        </button>
        {!isNew && (
          <ResourceDeleteConfirm
            resourceKind="skill"
            resourceName={skillName}
            disabled={isBundled}
            onConfirm={handleDelete}
          />
        )}
      </div>
      {error && (
        <div className="border-b border-error/30 bg-error/15 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <div className="w-56 overflow-auto border-r border-border bg-background p-2">
          <div className="mb-2 flex items-center justify-between px-1 text-xs text-text-secondary">
            <span>Files</span>
            {!isBundled && (
              <button
                onClick={(): void => {
                  setAddFileOpen(true);
                }}
                title="Add file"
              >
                <FilePlus className="size-3" />
              </button>
            )}
          </div>
          {sortedPaths.map(path => (
            <div
              key={path}
              role="button"
              tabIndex={0}
              className={`group flex cursor-pointer items-center justify-between rounded px-2 py-1 text-xs ${
                selected === path
                  ? 'bg-surface-elevated text-text-primary'
                  : 'text-text-secondary hover:bg-surface'
              }`}
              onClick={() => {
                setSelected(path);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelected(path);
                }
              }}
            >
              <span className="truncate">{path}</span>
              {!isBundled && path !== 'SKILL.md' && (
                <ConfirmDialog
                  trigger={
                    // stopPropagation prevents the click from bubbling to the
                    // parent row (which has its own onClick that changes
                    // `selected`). Radix's AlertDialogTrigger asChild composes
                    // event handlers in order, so this runs before Radix opens
                    // the dialog — both effects fire, but the row selection is
                    // suppressed. Do not remove without keeping the row from
                    // re-selecting when the user opens this confirm.
                    <button
                      className="hidden group-hover:inline text-error"
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      ×
                    </button>
                  }
                  title="Remove file?"
                  description={
                    <>
                      Remove <strong>{path}</strong> from this skill. The change isn't saved until
                      you click Save.
                    </>
                  }
                  confirmLabel="Remove"
                  onConfirm={(): void => {
                    handleDeleteFile(path);
                  }}
                />
              )}
            </div>
          ))}
        </div>
        <textarea
          className="flex-1 resize-none bg-background p-4 font-mono text-sm text-text-primary outline-none"
          value={files[selected] ?? ''}
          onChange={e => {
            const nextValue = e.target.value;
            setFiles(currentFiles => ({ ...currentFiles, [selected]: nextValue }));
          }}
          readOnly={isBundled}
        />
      </div>
      <PromptDialog
        open={addFileOpen}
        onOpenChange={setAddFileOpen}
        title="Add file"
        description="Add a new file to this skill. The file isn't saved until you click Save."
        label="File path"
        placeholder="rules/foo.md"
        submitLabel="Add"
        onSubmit={handleAddFile}
      />
    </div>
  );
}
